import React, { useEffect, useState } from 'react';
import { User, PaymentOrder } from '../types';
import { CreditCard, ShieldCheck, CheckCircle2, Lock, RefreshCw } from 'lucide-react';
import { api, PAY_STATUS_TEXT } from '../api';

interface PaymentsSectionProps {
  user: User | null;
  onJoinMembership: () => void;
  onRequireLogin: () => void;
  refreshKey: number;
}

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({ user, onJoinMembership, onRequireLogin, refreshKey }) => {
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await api<PaymentOrder[]>('my_orders');
      setOrders(data);
    } catch (ex: any) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user?.id, refreshKey]);

  const badge = (s: string) => {
    const cls: Record<string, string> = { unpaid: 'bg-amber-100 text-amber-800', paid: 'bg-emerald-100 text-emerald-800', refunding: 'bg-sky-100 text-sky-800', refunded: 'bg-slate-200 text-slate-600' };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${cls[s] || 'bg-slate-100 text-slate-600'}`}>{PAY_STATUS_TEXT[s] || s}</span>;
  };

  return (
    <section className="py-12 bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-100/80 text-sky-700 rounded-full text-xs font-bold mb-3">
            <CreditCard className="w-4 h-4" /><span>微信支付 · 官方安全收银台</span>
          </div>
          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">微信缴费中心</h2>
          <p className="text-slate-600 text-sm mt-2">会员会费在线缴纳、赛事报名费支付与退款进度查询，资金受微信支付官方监管。</p>
        </div>

        {!user ? (
          <div className="bg-white rounded-3xl border-2 border-dashed border-slate-200 p-16 text-center space-y-4 max-w-2xl mx-auto">
            <Lock className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-sm font-bold text-slate-700">缴费记录需登录后查看</p>
            <button onClick={onRequireLogin} className="px-8 py-3 bg-sky-600 text-white text-sm font-bold rounded-xl">去登录 / 注册</button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="p-6 bg-emerald-50/70 border border-emerald-200 rounded-2xl space-y-3">
                <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded">协会会员缴费</span>
                <h3 className="text-lg font-black text-slate-900">个人年度会员会费</h3>
                <div className="text-2xl font-black text-emerald-700">
                  ¥ {user.settings?.member_fee || '150'}.00 <span className="text-xs font-normal text-slate-500">/年</span>
                </div>
                {user.member_status === 'active' ? (
                  <div className="p-3 bg-emerald-100 border border-emerald-300 rounded-xl text-xs text-emerald-800 font-bold">
                    ✓ 您已是有效会员{user.member_id ? `（编号 ${user.member_id}）` : ''}，有效期至 {user.membership_expiry}
                  </div>
                ) : (
                  <button onClick={onJoinMembership} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow">
                    立即发起微信缴纳
                  </button>
                )}
              </div>

              <div className="p-6 bg-sky-50/70 border border-sky-200 rounded-2xl space-y-3">
                <span className="text-xs font-bold text-sky-800 bg-sky-100 px-2.5 py-1 rounded">赛事报名费缴费</span>
                <h3 className="text-lg font-black text-slate-900">水上赛事报名费用</h3>
                <p className="text-xs text-slate-500">在「游泳赛事」栏目选择赛事在线报名，按赛事项目自动计算（¥0 ~ ¥80 元/人次）</p>
                <a href="#/events" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('csx-nav', { detail: 'events' })); }}
                  className="block text-center w-full py-2.5 bg-sky-600 hover:bg-sky-500 text-white font-extrabold text-xs rounded-xl shadow">
                  前去选择赛事报名
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-900">我的缴费与退款记录</h3>
                <button onClick={load} className="flex items-center gap-1 text-xs text-sky-600 font-bold"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />刷新</button>
              </div>
              {err && <p className="text-xs text-red-600 mb-3">{err}</p>}
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-slate-700">
                  <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">订单号</th>
                      <th className="py-2.5 px-3">缴费项目</th>
                      <th className="py-2.5 px-3">金额</th>
                      <th className="py-2.5 px-3">状态</th>
                      <th className="py-2.5 px-3">支付时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">暂无缴费记录</td></tr>}
                    {orders.map((o) => (
                      <tr key={o.id}>
                        <td className="py-3 px-3 font-mono font-bold">{o.order_no}</td>
                        <td className="py-3 px-3">{o.title}</td>
                        <td className="py-3 px-3 font-bold">¥ {o.amount.toFixed(2)}</td>
                        <td className="py-3 px-3">{badge(o.status)}</td>
                        <td className="py-3 px-3">{o.pay_time || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="flex items-center gap-1.5 text-[10px] text-slate-400 mt-4">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                微信支付资金受银行全额监管 · 256-bit SSL 加密传输 · 支持原路退款
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};
